import type { Category } from "../models/Category"
import { request as __request } from "../core/request"
import { trackPromise } from "react-promise-tracker"

export class ProductCategoriesService {
  /**
   * Gets all Product Categories.
   * ## Requires:
   * ### productCategories.Read
   * @returns any Success
   * @throws ApiError
   */
  public static async productCategoriesGetAllAsync({
    select,
    expand,
    filter,
    parentId,
  }: {
    parentId: string
    /** Limits the properties returned in the result. **/
    select?: string
    /** Indicates the related entities to be represented inline. The maximum depth is 2. **/
    expand?: string
    filter?: string
  }): Promise<{
    readonly "@odata.context"?: string
    readonly value?: Array<Category>
  }> {
    const result = await trackPromise(
      __request({
        method: "GET",
        path: `/api/tenancies/${parentId}/isa/productcategories`,
        query: {
          $select: select,
          $expand: expand,
          $filter: filter,
        },
        errors: {
          400: `Bad request`,
          401: `Unauthorized`,
          403: `Forbidden`,
          406: `Not acceptable`,
          500: `Internal server error`,
        },
      })
    )
    return result.body
  }

  /**
   * Create Product Category.
   * ## Requires:
   * ### productCategories.Create
   * @returns any Success
   * @throws ApiError
   */
  public static async productCategoriesPostAsync({
    parentId,
    requestBody,
  }: {
    parentId: string
    requestBody?: Category
  }): Promise<
    {
      readonly "@odata.context"?: string
    } & Category
  > {
    const result = await trackPromise(
      __request({
        method: "POST",
        path: `/api/tenancies/${parentId}/isa/productcategories`,
        body: requestBody,
        errors: {
          400: `Bad request`,
          401: `Unauthorized`,
          403: `Forbidden`,
          406: `Not acceptable`,
          500: `Internal server error`,
        },
      })
    )
    return result.body
  }

  /**
   * Gets Product Categories.
   * ## Requires:
   * ### productCategories.Read
   * @returns any Success
   * @throws ApiError
   */
  public static async productCategoriesGetAsync({
    id,
    parentId,
    select,
    expand,
  }: {
    parentId: string
    id: string
    /** Limits the properties returned in the result. **/
    select?: string
    /** Indicates the related entities to be represented inline. The maximum depth is 2. **/
    expand?: string
  }): Promise<
    {
      readonly "@odata.context"?: string
    } & Category
  > {
    const result = await trackPromise(
      __request({
        method: "GET",
        path: `/api/tenancies/${parentId}/isa/productcategories/${id}`,
        query: {
          $select: select,
          $expand: expand,
        },
        errors: {
          400: `Bad request`,
          401: `Unauthorized`,
          403: `Forbidden`,
          406: `Not acceptable`,
          500: `Internal server error`,
        },
      })
    )
    return result.body
  }

  /**
   * Update Product Category.
   * ## Requires:
   * ### productCategories.Update
   * @returns any Success
   * @throws ApiError
   */
  public static async productCategoriesPatchAsync({
    parentId,
    id,
    requestBody,
  }: {
    parentId: string
    id: string
    requestBody?: Category
  }): Promise<
    {
      readonly "@odata.context"?: string
    } & Category
  > {
    const result = await trackPromise(
      __request({
        method: "PATCH",
        path: `/api/tenancies/${parentId}/isa/productcategories/${id}`,
        body: requestBody,
        errors: {
          400: `Bad request`,
          401: `Unauthorized`,
          403: `Forbidden`,
          406: `Not acceptable`,
          409: `Conflict`,
          500: `Internal server error`,
        },
      })
    )
    return result.body
  }

  /**
   * Delete the Product Category.
   * ## Requires:
   * ### productCategories.Delete
   * @returns any Success
   * @throws ApiError
   */
  public static async productCategoriesDeleteAsync({ id, parentId }: { id: string; parentId: string }): Promise<
    {
      readonly "@odata.context"?: string
    } & Category
  > {
    const result = await trackPromise(
      __request({
        method: "DELETE",
        path: `/api/tenancies/${parentId}/isa/productcategories/${id}`,
        errors: {
          400: `Bad request`,
          401: `Unauthorized`,
          403: `Forbidden`,
          406: `Not acceptable`,
          500: `Internal server error`,
        },
      })
    )
    return result.body
  }
}
